import React, { useMemo } from "react";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

/**
 * Props:
 *  - title, page, pages, items, loading, onAdd, onPageChange, headers, renderRow, subtitle
 * Optional:
 *  - addActions, addButtonProps, loadingOverlay
 *  - variant: "light" | "dark"
 *  - className
 *
 * NEW optional:
 *  - showPageNumbers (default true)
 *  - pageWindow (default 2)  -> how many pages to show on each side of current
 */
export default function PaginatedFlatTable({
                                               title,
                                               page,
                                               pages,
                                               items,
                                               loading,
                                               onAdd,
                                               onPageChange,
                                               headers,
                                               renderRow,
                                               subtitle,
                                               totalCount,

                                               addActions,
                                               addButtonProps,
                                               loadingOverlay = true,
                                               variant = "light",
                                               className = "",

                                               showPageNumbers = true,
                                               pageWindow = 2,
                                               addButtonTitle,
                                               headerRightExtras = null,
                                           }) {
    const { t } = useTranslation();
    const isDark = variant === "dark";
    const colors = useThemeColors();

    const resolvedSubtitle = subtitle || t("table.subtitle");
    const resolvedAddButtonTitle = addButtonTitle || t("table.add");

    const scopeId = useMemo(() => {
        const safe = String(title || "table")
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, "-")
            .replace(/(^-|-$)/g, "");
        return `kbola-pft-${safe || "x"}`;
    }, [title]);

    const ui = useMemo(() => {
        const primary = colors.primary;
        const textOnPrimary = colors.textOnPrimary;

        const light = {
            wrap: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.10)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "12px 14px",
                background: `linear-gradient(
          135deg,
          ${alphaFromHex(primary, 0.52)},
          ${alphaFromHex(primary, 0.00)}
        )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 14,
                fontWeight: 900,
                color: textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            sub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: colors.textOnPrimary,
            },
            headerRight: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                flex: "0 0 auto",
            },
            btn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
            },
            btnPrimary: {
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
            },

            body: { padding: 18 },
            tableWrap: { borderRadius: 14, position: "relative", overflowX: "auto", overflowY: "hidden", border: "1px solid rgba(0,0,0,0.07)" },
            overlay: {
                position: "absolute",
                inset: 0,
                background: "rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 10,
            },
            overlayText: { color: "rgba(20,21,26,0.55)", fontWeight: 800 },

            footer: {
                padding: "12px 14px",
                borderTop: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                background: "#fff",
            },
            pagerBtn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
            },
            pagerText: { fontWeight: 900, color: "rgba(20,21,26,0.70)", fontSize: 12.5 },
            totalPill: {
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                borderRadius: 999,
                padding: "8px 12px",
                border: "1px solid rgba(0,0,0,0.08)",
                background: alphaFromHex(colors.primary, 0.10),
                color: "rgba(20,21,26,0.82)",
                whiteSpace: "nowrap",
            },
            totalLabel: {
                fontSize: 11.5,
                fontWeight: 900,
                letterSpacing: "0.03em",
                textTransform: "uppercase",
                color: "rgba(20,21,26,0.58)",
            },
            totalValue: {
                fontSize: 13,
                fontWeight: 900,
                color: colors.primary,
            },
            emptyCard: {
                borderRadius: 14,
                padding: 14,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.015)",
                textAlign: "center",
                color: "rgba(20,21,26,0.60)",
                fontWeight: 800,
            },

            pageStrip: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
            pageBtn: {
                borderRadius: 10,
                padding: "8px 10px",
                fontWeight: 900,
                border: "1px solid rgba(0,0,0,0.10)",
                background: colors.surface,
                color: colors.textMuted,
                cursor: "pointer",
                minWidth: 38,
                textAlign: "center",
            },
            pageBtnActive: {
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
            },
            pageEllipsis: { fontWeight: 900, color: "rgba(20,21,26,0.55)", padding: "0 2px" },

            dropdownBtn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
                background: primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: textOnPrimary,
            },
        };

        const dark = {
            ...light,
            wrap: {
                ...light.wrap,
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(0,0,0,0.18)",
                boxShadow: "0 18px 44px rgba(0,0,0,0.30)",
            },
            header: {
                ...light.header,
                background: `radial-gradient(
            1100px 380px at 10% 0%,
            ${alphaFromHex(primary, 0.50)},
            rgba(0,0,0,0) 55%
          ),
          linear-gradient(180deg, rgba(255,255,255,0.04), rgba(0,0,0,0))`,
                borderBottom: "1px solid rgba(255,255,255,0.08)",
            },
            title: { ...light.title, color: "rgba(255,255,255,0.90)" },
            sub: { ...light.sub, color: "rgba(255,255,255,0.70)" },
            btn: {
                ...light.btn,
                border: "1px solid rgba(255,255,255,0.14)",
                background: "rgba(255,255,255,0.06)",
                color: "rgba(255,255,255,0.90)",
            },
            footer: {
                ...light.footer,
                background: "rgba(0,0,0,0.10)",
                borderTop: "1px solid rgba(255,255,255,0.08)",
            },
            pagerBtn: {
                ...light.pagerBtn,
                border: "1px solid rgba(255,255,255,0.14)",
                background: "rgba(255,255,255,0.06)",
                color: "rgba(255,255,255,0.90)",
            },
            pagerText: { ...light.pagerText, color: "rgba(255,255,255,0.70)" },
            totalPill: {
                ...light.totalPill,
                border: "1px solid rgba(255,255,255,0.10)",
                background: alphaFromHex(primary, 0.20),
                color: "rgba(255,255,255,0.88)",
            },
            totalLabel: {
                ...light.totalLabel,
                color: "rgba(255,255,255,0.62)",
            },
            totalValue: {
                ...light.totalValue,
                color: "rgba(255,255,255,0.92)",
            },
            emptyCard: {
                ...light.emptyCard,
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.04)",
                color: "rgba(255,255,255,0.72)",
            },
            pageBtn: {
                ...light.pageBtn,
                border: "1px solid rgba(255,255,255,0.14)",
                background: "rgba(255,255,255,0.06)",
                color: "rgba(255,255,255,0.86)",
            },
            pageEllipsis: { ...light.pageEllipsis, color: "rgba(255,255,255,0.65)" },
        };

        return isDark ? dark : light;
    }, [isDark, colors]);

    const safeItems = Array.isArray(items) ? items : [];
    const count = safeItems.length;
    const displayCount = totalCount ?? count;

    const currentPage = Math.max(1, parseInt(page || 1, 10));
    const totalPages = Math.max(1, parseInt(pages || 1, 10));

    const canPrev = currentPage > 1;
    const canNext = currentPage < totalPages;

    const hasMultiActions = Array.isArray(addActions) && addActions.length > 0;

    const AddArea = () => {
        if (hasMultiActions) {
            return (
                <div style={ui.headerRight}>
                    <div className="btn-group">
                        <button
                            type="button"
                            className="dropdown-toggle"
                            data-toggle="dropdown"
                            aria-haspopup="true"
                            aria-expanded="false"
                            style={{ ...ui.dropdownBtn, opacity: loading ? 0.75 : 1 }}
                            disabled={loading}
                            title={t("table.add")}
                        >
                            <i className="fas fa-plus" />
                            {resolvedAddButtonTitle}
                            <span className="sr-only">{t("table.toggleDropdown")}</span>
                        </button>

                        <div className="dropdown-menu dropdown-menu-right kbola-add-menu" role="menu">
                            {addActions.map((act, i) => (
                                <button
                                    key={`add-action-${i}`}
                                    className="dropdown-item kbola-add-item"
                                    type="button"
                                    onClick={(e) => act?.onClick?.(e)}
                                    {...(act?.props || {})}
                                >
                                    {act?.iconClass ? <i className={`${act.iconClass} mr-2`} /> : null}
                                    {act?.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            );
        }

        if (onAdd) {
            return (
                <div style={ui.headerRight}>
                    <button
                        type="button"
                        style={{ ...ui.btn, ...ui.btnPrimary, opacity: loading ? 0.75 : 1 }}
                        onClick={onAdd}
                        disabled={loading}
                        title={resolvedAddButtonTitle}
                        {...(addButtonProps || {})}
                    >
                        <i className="fas fa-plus" />
                        {resolvedAddButtonTitle}
                    </button>
                </div>
            );
        }

        return null;
    };

    const goToPage = (p) => {
        const target = Math.min(totalPages, Math.max(1, parseInt(p, 10)));
        if (loading) return;
        if (target === currentPage) return;
        onPageChange?.(target);
    };

    const pageModel = useMemo(() => {
        if (!showPageNumbers) return [];

        if (totalPages <= 10) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const windowStart = Math.max(2, currentPage - pageWindow);
        const windowEnd = Math.min(totalPages - 1, currentPage + pageWindow);

        const out = [1];

        if (windowStart > 2) out.push("…");

        for (let p = windowStart; p <= windowEnd; p++) out.push(p);

        if (windowEnd < totalPages - 1) out.push("…");

        out.push(totalPages);

        return out;
    }, [showPageNumbers, totalPages, currentPage, pageWindow]);

    return (
        <div id={scopeId} style={ui.wrap} className={className}>
            <div style={ui.header}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <span style={ui.badge}>{count}</span>
                    <div style={{ minWidth: 0 }}>
                        <h3 style={ui.title} title={title}>
                            {title}
                        </h3>
                        <p style={ui.sub}>{resolvedSubtitle}</p>
                    </div>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {headerRightExtras}
                    <AddArea />
                </div>
            </div>

            <div style={ui.body}>
                <div className="table-responsive" style={{ ...ui.tableWrap }}>
                    {loadingOverlay && loading ? (
                        <div style={ui.overlay}>
                            <div className="text-center">
                                <div className="spinner-border" role="status" aria-label={t("table.loading")} />
                                <div className="mt-2" style={ui.overlayText}>
                                    {t("table.loading")}
                                </div>
                            </div>
                        </div>
                    ) : null}

                    <table className="table mb-0 kbola-table">
                        <thead>
                        <tr>
                            {headers?.map((h, idx) => (
                                <th
                                    key={`${h}-${idx}`}
                                    className={idx === (headers?.length || 0) - 1 ? "text-right" : ""}
                                >
                                    {h}
                                </th>
                            ))}
                        </tr>
                        </thead>

                        <tbody>
                        {!loadingOverlay && loading ? (
                            <tr>
                                <td colSpan={headers?.length || 1}>
                                    <div style={ui.emptyCard}>{t("table.loading")}</div>
                                </td>
                            </tr>
                        ) : null}

                        {!loading && safeItems.length === 0 ? (
                            <tr>
                                <td colSpan={headers?.length || 1}>
                                    <div
                                        style={{
                                            ...ui.emptyCard,
                                            minHeight: 280,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                        }}
                                    >
                                        {t("table.noRecords")}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            safeItems.map(renderRow)
                        )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div style={ui.footer}>
                <button
                    type="button"
                    style={{ ...ui.pagerBtn, opacity: canPrev ? 1 : 0.5 }}
                    onClick={() => canPrev && goToPage(currentPage - 1)}
                    disabled={!canPrev || loading}
                    title={t("table.previousPage")}
                >
                    <i className="fas fa-arrow-left" />
                    {t("table.prev")}
                </button>

                <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                    {showPageNumbers ? (
                        <div style={ui.pageStrip} aria-label={t("table.pagination")}>
                            {pageModel.map((x, idx) => {
                                if (x === "…") {
                                    return (
                                        <span key={`ellipsis-${idx}`} style={ui.pageEllipsis} aria-hidden="true">
                                            …
                                        </span>
                                    );
                                }
                                const p = x;
                                const isActive = p === currentPage;
                                return (
                                    <button
                                        key={`p-${p}`}
                                        type="button"
                                        style={{ ...ui.pageBtn, ...(isActive ? ui.pageBtnActive : {}) }}
                                        onClick={() => goToPage(p)}
                                        disabled={loading}
                                        aria-current={isActive ? "page" : undefined}
                                        title={t("table.goToPage", { page: p })}
                                    >
                                        {p}
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div style={ui.pagerText}>
                            {t("table.page")} {currentPage} {t("table.of")} {totalPages}
                        </div>
                    )}
                    <div style={ui.totalPill}>
                        <span style={ui.totalLabel}>{t("table.total")}</span>
                        <span style={ui.totalValue}>{displayCount}</span>
                    </div>
                </div>

                <button
                    type="button"
                    style={{ ...ui.pagerBtn, opacity: canNext ? 1 : 0.5 }}
                    onClick={() => canNext && goToPage(currentPage + 1)}
                    disabled={!canNext || loading}
                    title={t("table.nextPage")}
                >
                    {t("table.next")}
                    <i className="fas fa-arrow-right" />
                </button>
            </div>

            <style>{`
        /* TABLE */
        #${scopeId} .kbola-table thead th {
          background: ${isDark ? "rgba(255,255,255,0.07)" : "#f7f9fc"};
          border-bottom: 1px solid ${
                isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"
            };
          color: ${isDark ? "rgba(255,255,255,0.74)" : "rgba(20,21,26,0.72)"};
          font-size: 12.5px;
          font-weight: 900;
          text-transform: uppercase;
          letter-spacing: 0.02em;
          padding: 14px 16px;
          white-space: nowrap;
        }

        #${scopeId} .kbola-table {
          min-width: 980px;
          margin-bottom: 0;
        }

        #${scopeId} .table-responsive::-webkit-scrollbar {
          height: 9px;
        }

        #${scopeId} .table-responsive::-webkit-scrollbar-track {
          background: ${isDark ? "rgba(255,255,255,0.06)" : "rgba(20,21,26,0.05)"};
          border-radius: 999px;
        }

        #${scopeId} .table-responsive::-webkit-scrollbar-thumb {
          background: ${alphaFromHex(colors.primary, 0.52)};
          border-radius: 999px;
          border: 2px solid ${isDark ? "rgba(20,21,26,0.20)" : "#fff"};
        }

        #${scopeId} .kbola-table tbody td {
          border-top: 1px solid ${
                isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"
            };
          padding: 15px 16px;
          vertical-align: middle;
          color: ${isDark ? "rgba(255,255,255,0.86)" : "rgba(20,21,26,0.86)"};
          font-weight: 700;
          font-size: 13.5px;
        }

        #${scopeId} .kbola-table tbody tr:nth-child(even) {
          background: ${isDark ? "rgba(255,255,255,0.018)" : "rgba(20,21,26,0.012)"};
        }

        #${scopeId} .kbola-table tbody tr:hover {
          background: ${
                isDark ? "rgba(var(--kbs-primary-rgb, 0,173,255), 0.08)" : "rgba(var(--kbs-primary-rgb, 0,173,255), 0.10)"
            };
        }

        @media (max-width: 700px) {
          #${scopeId} > div:first-child { flex-wrap: wrap; }
          #${scopeId} > div:first-child > div:last-child { width: 100%; justify-content: flex-end; }
          #${scopeId} .kbola-table { min-width: 980px; }
          #${scopeId} .kbola-table tbody td { padding: 13px 12px; font-size: 13px; }
          #${scopeId} .kbola-table thead th { padding: 12px; font-size: 11px; }
          #${scopeId} > div:nth-child(2) { padding: 12px; }
        }

        #${scopeId} button:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
        }

        #${scopeId} .kbola-add-menu{
          border-radius: 14px;
          border: 1px solid ${
                isDark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.10)"
            };
          box-shadow: 0 18px 44px rgba(0,0,0,0.18);
          padding: 6px;
          min-width: 220px;
          background: ${isDark ? "rgba(20,21,26,0.98)" : "#fff"};
        }

        #${scopeId} .kbola-add-item{
          border-radius: 12px;
          font-weight: 900;
          padding: 10px 10px;
          display: flex;
          align-items: center;
          color: ${
                isDark ? "rgba(255,255,255,0.88)" : "rgba(20,21,26,0.88)"
            };
        }

        #${scopeId} .kbola-add-item:hover{
          background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.22);
          color: #14151a;
        }

        #${scopeId} i.fas,
        #${scopeId} i.far,
        #${scopeId} i.fal,
        #${scopeId} i.fa,
        #${scopeId} i[class^="fa-"],
        #${scopeId} i[class*=" fa-"] {
          font-family: "Font Awesome 5 Free" !important;
          font-weight: 900;
        }

        #${scopeId} i.fab {
          font-family: "Font Awesome 5 Brands" !important;
          font-weight: 400;
        }
      `}</style>
        </div>
    );
}
